import React, { JSX, lazy, Suspense } from "react";

import FallbackLoading from "./components/FallbackLoading";
import NotFound from "./components/NotFound";
import AdminAccount from "./pages/admin/account/account";
import AdminAccountActivity from "./pages/admin/account/activities/activities";
import AdminAccountUser from "./pages/admin/account/user/user";
import AdminAccountUserOnline from "./pages/admin/account/user-online/user-online";
import Admin from "./pages/admin/admin";
import AdminECP from "./pages/admin/ecp/ecp";
import ProfileSetting from "./pages/admin/profile-settings";
import AdminSettingIssue from "./pages/admin/setting/issue/issue";
import AdminSetting from "./pages/admin/setting/setting";
import AdminSettingTag from "./pages/admin/setting/tag/tag";
import AdminSettingTagCategory from "./pages/admin/setting/tag-category/tag-category";

const AdminDashboard = lazy(() => import("./pages/admin/dashboard/dashboard"));
const AdminCallCenter = lazy(
  () => import("./pages/admin/dashboard/call-center"),
);
const AdminECPList = lazy(() => import("./pages/admin/ecp/list"));
const AdminECPCreate = lazy(() => import("./pages/admin/ecp/create"));
const AdminECPEdit = lazy(() => import("./pages/admin/ecp/edit"));
const AdminSettingIssueList = lazy(
  () => import("./pages/admin/setting/issue/list"),
);
const AdminSettingTagList = lazy(
  () => import("./pages/admin/setting/tag/list"),
);
const AdminSettingTagCreate = lazy(
  () => import("./pages/admin/setting/tag/create"),
);
const AdminSettingTagEdit = lazy(
  () => import("./pages/admin/setting/tag/edit"),
);
const AdminSettingTagCategoryList = lazy(
  () => import("./pages/admin/setting/tag-category/list"),
);
const AdminSettingTagCategoryCreate = lazy(
  () => import("./pages/admin/setting/tag-category/create"),
);
const AdminSettingTagCategoryEdit = lazy(
  () => import("./pages/admin/setting/tag-category/edit"),
);
const AdminAccountActivityList = lazy(
  () => import("./pages/admin/account/activities/list"),
);
const AdminAccountUserList = lazy(
  () => import("./pages/admin/account/user/list"),
);
const AdminAccountUserOnlineList = lazy(
  () => import("./pages/admin/account/user-online/list"),
);

const WithSuspense = (
  Component: React.LazyExoticComponent<() => JSX.Element>,
) => (
  <Suspense fallback={<FallbackLoading />}>
    <Component />
  </Suspense>
);

export const adminRoutes = [
  {
    path: "admin",
    element: <Admin />,
    children: [
      {
        path: "profile-settings",
        element: <ProfileSetting />,
      },
      {
        path: "",
        element: WithSuspense(AdminDashboard),
      },
      {
        path: "call-center",
        element: WithSuspense(AdminCallCenter),
      },
      {
        path: "ecp",
        element: <AdminECP />,
        children: [
          {
            path: "",
            element: WithSuspense(AdminECPList),
          },
          {
            path: "create",
            element: WithSuspense(AdminECPCreate),
          },
          {
            path: "edit/:id",
            element: WithSuspense(AdminECPEdit),
          },
        ],
      },
      {
        path: "setting",
        element: <AdminSetting />,
        children: [
          {
            path: "issue",
            element: <AdminSettingIssue />,
            children: [
              {
                path: "",
                element: WithSuspense(AdminSettingIssueList),
              },
            ],
          },
          {
            path: "tag",
            element: <AdminSettingTag />,
            children: [
              {
                path: "",
                element: WithSuspense(AdminSettingTagList),
              },
              {
                path: "create",
                element: WithSuspense(AdminSettingTagCreate),
              },
              {
                path: "edit/:id",
                element: WithSuspense(AdminSettingTagEdit),
              },
            ],
          },
          {
            path: "tag-category",
            element: <AdminSettingTagCategory />,
            children: [
              {
                path: "",
                element: WithSuspense(AdminSettingTagCategoryList),
              },
              {
                path: "create",
                element: WithSuspense(AdminSettingTagCategoryCreate),
              },
              {
                path: "edit/:id",
                element: WithSuspense(AdminSettingTagCategoryEdit),
              },
            ],
          },
        ],
      },
      {
        path: "account",
        element: <AdminAccount />,
        children: [
          {
            path: "user",
            element: <AdminAccountUser />,
            children: [
              {
                path: "",
                element: WithSuspense(AdminAccountUserList),
              },
            ],
          },
          {
            path: "user-online",
            element: <AdminAccountUserOnline />,
            children: [
              {
                path: "",
                element: WithSuspense(AdminAccountUserOnlineList),
              },
            ],
          },
          {
            path: "activities",
            element: <AdminAccountActivity />,
            children: [
              {
                path: "",
                element: WithSuspense(AdminAccountActivityList),
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
];

export default adminRoutes;
